import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import CustomerPhonesCard from "@/components/CustomerPhonesCard";
import {
  ArrowLeft, Building2, CreditCard, FileText, Activity,
  Package, Wallet as WalletIcon, FileSignature, ToggleLeft, ToggleRight,
  Save, Loader2,
} from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import CustomerBilling from "@/pages/admin/CustomerBilling";
import { toast } from "sonner";

const CUSTOMER_TYPE_BADGE = {
  RETAIL: "bg-blue-50 text-blue-700 border-blue-200",
  B2C: "bg-purple-50 text-purple-700 border-purple-200",
  B2B: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function Card({ title, icon: Icon, children, testid }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="w-4 h-4 text-brand-500" />}
        <h3 className="font-heading text-sm font-bold text-slate-900 uppercase tracking-wide">{title}</h3>
      </div>
      <div>{children}</div>
    </div>
  );
}

function Field({ label, value, name, editing, onChange, type = "text", readonly }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-2 border-b border-slate-50 last:border-0">
      <div className="text-xs text-slate-500 uppercase font-semibold">{label}</div>
      {editing && !readonly ? (
        <input
          type={type}
          value={value || ""}
          onChange={(e) => onChange(name, e.target.value)}
          data-testid={`customer-field-${name}`}
          className="col-span-2 text-sm text-slate-800 border border-slate-200 rounded px-2 py-1 focus:outline-none focus:border-brand-500"
        />
      ) : (
        <div className="col-span-2 text-sm text-slate-800">{value || "—"}</div>
      )}
    </div>
  );
}

export default function CustomerProfile() {
  const { id } = useParams();
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const base = isAdmin ? "/admin" : "/staff";
  const [orig, setOrig] = useState({});
  const [reason, setReason] = useState("");
  const [history, setHistory] = useState([]);
  const [pending, setPending] = useState([]);
  const [c360, setC360] = useState(null);
  const [profile, setProfile] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [creditEnabled, setCreditEnabled] = useState(false);
  const [creditLimit, setCreditLimit] = useState(0);
  const [creditDays, setCreditDays] = useState(30);

  const load = async () => {
    setLoading(true);
    try {
      // Customer 360 works for every customer (including walk-ins that have no profile record yet)
      let r360 = null;
      try { r360 = (await api.get(`/v2/customer-360/${id}`)).data; setC360(r360); } catch { /* handled below */ }
      let r;
      try { r = await api.get(`/v2/customers/profile/${id}`); }
      catch (err) {
        if (!r360) throw err;
        const u = r360.user || {};
        r = { data: { ...(r360.profile || {}), customer_id: id, customer_code: r360.customer_code, contact_person: u.name, mobile: u.phone, email: u.email,
                      company_name: u.company_name || "", status: "ACTIVE", user: u, wallet_balance: r360.wallet?.balance || 0,
                      customer_type: r360.segment === "WALKIN" ? "WALK-IN" : (u.customer_type || "RETAIL") } };
      }
      setProfile(r.data);
      const f0 = {
        company_name: r.data.company_name || "",
        business_type: r.data.business_type || "",
        manual_rate_card: !!r.data.manual_rate_card,
        business_address: r.data.business_address || "",
        "bank.account_holder_name": r.data.bank?.account_holder_name || "",
        "bank.account_number": r.data.bank?.account_number || "",
        "bank.ifsc_code": r.data.bank?.ifsc_code || "",
        "bank.bank_name": r.data.bank?.bank_name || "",
        "rate_agreement.markup_percent": r.data.rate_agreement?.markup_percent ?? "",
        contact_person: r.data.contact_person || r.data.user?.name || "",
        mobile: r.data.mobile || r.data.user?.phone || "",
        email: r.data.email || r.data.user?.email || "",
        gst_number: r.data.gst_number || "",
        pan_number: r.data.pan_number || "",
        address_line1: r.data.address_line1 || "",
        address_line2: r.data.address_line2 || "",
        city: r.data.city || "",
        state: r.data.state || "",
        pincode: r.data.pincode || "",
        billing_cycle: r.data.billing_cycle || "MONTHLY",
        platform_fee_enabled: r.data.platform_fee_enabled !== false,
      };
      setForm(f0); setOrig(f0);
      setCreditEnabled(!!r.data.user?.wallet_credit_enabled);
      setCreditLimit(r.data.user?.credit_limit || 0);
      setCreditDays(r.data.user?.credit_days || 30);
    } catch (e) {
      toast.error("Failed to load customer profile");
    }

    try {
      const h = await api.get(`/v2/customer-changes/history/${id}`);
      setHistory(h.data || []);
      const pq = await api.get("/v2/customer-changes", { params: { status: "PENDING" } });
      setPending((pq.data || []).filter((x) => x.customer_id === id));
    } catch { /* optional */ }

    // Try to fetch rate agreement (optional)
    try {
      const ag = await api.get("/v2/rate-agreements", { params: { customer_id: id } });
      const list = ag.data?.items || ag.data || [];
      const active = (Array.isArray(list) ? list : []).find((a) => a.status === "ACTIVE") || (Array.isArray(list) ? list[0] : null);
      setAgreement(active || null);
    } catch { /* optional */ }

    setLoading(false);
  };

  useEffect(() => { load(); }, [id]);

  const onFieldChange = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const changes = {};
      Object.keys(form).forEach((k) => { if (String(form[k] ?? "") !== String(orig[k] ?? "")) changes[k] = form[k]; });
      if (!Object.keys(changes).length) { toast.info("Nothing changed"); setEditing(false); return; }
      const { data } = await api.patch(`/v2/customer-changes/${id}`, { changes, reason });
      if (data.mode === "requested") toast.success("Sent to admin for approval. The customer is unchanged until it is approved.", { duration: 8000 });
      else toast.success("Customer updated");
      setEditing(false); setReason("");
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const decideNew = async (kind) => {
    let comment = "";
    if (kind === "reject") { comment = window.prompt("Reason for rejecting this customer (required):") || ""; if (!comment.trim()) return; }
    else if (!window.confirm("Approve this customer? They become ACTIVE with the rate agreement they were created with.")) return;
    try { await api.post(`/v2/customer-changes/new-customers/${id}/${kind}`, { comment }); toast.success(kind === "approve" ? "Customer approved" : "Customer rejected"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleCredit = async () => {
    if (!isAdmin) { toast.error("Only admin can change the credit wallet. Ask admin, or send a credit-limit change for approval."); return; }
    const next = !creditEnabled;
    try {
      await api.put(`/v2/wallets/${id}/credit-toggle`, {
        enabled: next, reason: "admin toggle",
        credit_limit: next ? (creditLimit || 0) : undefined,
      });
      toast.success(`Credit wallet ${next ? "enabled" : "disabled"}`);
      setCreditEnabled(next);
      load();
    } catch (e) {
      toast.error("Toggle failed");
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-brand-500" />
        </div>
      </DashboardLayout>
    );
  }

  if (!profile) {
    return (
      <DashboardLayout>
        <div className="text-center py-20">
          <p className="text-slate-500">Customer not found.</p>
          <Link to={`${base}/customers`} className="text-brand-500 text-sm font-semibold">← Back to customers</Link>
        </div>
      </DashboardLayout>
    );
  }

  const wallet = profile.wallet_balance ?? profile.user?.wallet_balance ?? 0;
  const accountMode = profile.user?.account_mode || (creditEnabled ? "CREDIT" : "PREPAID");
  const customerType = profile.customer_type || "RETAIL";

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to={`${base}/customers`} data-testid="customer-back-btn"
              className="p-2 rounded-md hover:bg-slate-100 text-slate-500">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-900">
                {profile.company_name || profile.contact_person || "Customer"}
              </h1>
              <p className="text-slate-500 text-xs">
                Customer ID: <span className="font-mono font-bold text-slate-700" data-testid="customer-code">{c360?.customer_code || profile.customer_code || profile.user?.customer_code || "pending"}</span>{c360?.segment === "WALKIN" && <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">WALK-IN</span>}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {!editing ? (
              <button onClick={() => setEditing(true)} data-testid="customer-edit-btn"
                className="px-4 py-2 bg-brand-500 text-white text-sm font-semibold rounded">{isAdmin ? "Edit" : "Edit (needs admin approval)"}</button>
            ) : (
              <>
                <button onClick={() => { setEditing(false); load(); }} className="px-3 py-2 border text-sm rounded">Cancel</button>
                <button onClick={save} disabled={saving} data-testid="customer-save-btn"
                  className="px-4 py-2 bg-brand-500 text-white text-sm font-semibold rounded flex items-center gap-2">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {isAdmin ? "Save" : "Send for approval"}
                </button>
              </>
            )}
          </div>
        </div>

        {profile.status === "PENDING_APPROVAL" && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3" data-testid="customer-pending-banner">
            <div className="text-sm text-amber-900"><b>New customer awaiting approval.</b> Check KYC, GSTIN and the rate agreement below
              {profile.rate_agreement?.rate_card_filename && <> (rate card: <a className="underline" href="#dl" onClick={async (e) => { e.preventDefault(); const r = await api.get(`/v2/customer-changes/new-customers/${id}/rate-card`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = profile.rate_agreement.rate_card_filename; a.click(); URL.revokeObjectURL(u); }}>{profile.rate_agreement.rate_card_filename}</a>)</>}.</div>
            {isAdmin ? (
              <div className="flex gap-2">
                <button onClick={() => decideNew("approve")} className="px-3 py-1.5 bg-emerald-600 text-white text-sm font-semibold rounded">Approve</button>
                <button onClick={() => decideNew("reject")} className="px-3 py-1.5 bg-red-600 text-white text-sm font-semibold rounded">Reject</button>
              </div>
            ) : <span className="text-xs text-amber-800">Only admin can approve.</span>}
          </div>
        )}
        {profile.status === "REJECTED" && (
          <div className="bg-red-50 border border-red-300 rounded-lg p-3 text-sm text-red-800">Rejected: {profile.rejected_reason || "no reason recorded"}</div>
        )}
        {pending.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900" data-testid="customer-pending-changes">
            {pending.length} change request(s) waiting for admin approval
            {pending.map((r) => <div key={r.id} className="text-xs mt-1">by {r.requested_by}: {r.changes.map((c) => `${c.label}: ${String(c.old ?? "—")} → ${String(c.new)}`).join("; ")}</div>)}
            {isAdmin && <Link to="/admin/customer-approvals" className="underline text-xs">Review in Approvals →</Link>}
          </div>
        )}
        {editing && !isAdmin && (
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Why is this change needed? (shown to the admin)"
            className="w-full border border-slate-300 rounded-lg p-2 text-sm" data-testid="customer-change-reason" />
        )}

        <CustomerPhonesCard customerId={id} isAdmin={isAdmin} />

        {c360 && <CustomerBilling data={c360} isAdmin={isAdmin} />}

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Card title="Business Info" icon={Building2} testid="customer-card-business">
            <Field label="Company Name" name="company_name" value={form.company_name} editing={editing} onChange={onFieldChange} />
            <Field label="Contact Person" name="contact_person" value={form.contact_person} editing={editing} onChange={onFieldChange} />
            <Field label="Mobile" name="mobile" value={form.mobile} editing={editing} onChange={onFieldChange} />
            <Field label="Email" name="email" value={form.email} editing={editing} onChange={onFieldChange} readonly />
            <Field label="GSTIN" name="gst_number" value={form.gst_number} editing={editing} onChange={onFieldChange} />
            <Field label="PAN" name="pan_number" value={form.pan_number} editing={editing} onChange={onFieldChange} />
            <Field label="Business Type" name="business_type" value={form.business_type} editing={editing} onChange={onFieldChange} />
            <Field label="Business Address" name="business_address" value={form.business_address} editing={editing} onChange={onFieldChange} />
            <Field label="Address 1" name="address_line1" value={form.address_line1} editing={editing} onChange={onFieldChange} />
            <Field label="Address 2" name="address_line2" value={form.address_line2} editing={editing} onChange={onFieldChange} />
            <div className="grid grid-cols-3 gap-2 mt-1">
              <Field label="City" name="city" value={form.city} editing={editing} onChange={onFieldChange} />
              <Field label="State" name="state" value={form.state} editing={editing} onChange={onFieldChange} />
              <Field label="Pincode" name="pincode" value={form.pincode} editing={editing} onChange={onFieldChange} />
            </div>
          </Card>

          <Card title="Account Settings" icon={CreditCard} testid="customer-card-account">
            <div className="grid grid-cols-3 gap-2 py-2 border-b border-slate-50">
              <div className="text-xs text-slate-500 uppercase font-semibold">Customer Type</div>
              <div className="col-span-2">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${CUSTOMER_TYPE_BADGE[customerType] || ""}`}>
                  {customerType}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 py-2 border-b border-slate-50">
              <div className="text-xs text-slate-500 uppercase font-semibold">Wallet Type</div>
              <div className="col-span-2 text-sm font-semibold text-slate-800">{accountMode}</div>
            </div>
            <div className="grid grid-cols-3 gap-2 py-2 border-b border-slate-50">
              <div className="text-xs text-slate-500 uppercase font-semibold">Wallet Balance</div>
              <div className="col-span-2 text-sm font-bold text-emerald-700">₹{(wallet || 0).toFixed(2)}</div>
            </div>
            <div className="grid grid-cols-3 gap-2 py-2 border-b border-slate-50 items-center">
              <div className="text-xs text-slate-500 uppercase font-semibold">Credit Wallet</div>
              <div className="col-span-2">
                <button onClick={toggleCredit} data-testid="customer-credit-toggle"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                  {creditEnabled ? <ToggleRight className="w-5 h-5 text-emerald-500" /> : <ToggleLeft className="w-5 h-5 text-slate-300" />}
                  {creditEnabled ? "Enabled" : "Disabled"}
                </button>
              </div>
            </div>
            {creditEnabled && (
              <>
                <div className="grid grid-cols-3 gap-2 py-2 border-b border-slate-50 items-center">
                  <div className="text-xs text-slate-500 uppercase font-semibold">Credit Limit (₹)</div>
                  <div className="col-span-2">
                    <input type="number" value={creditLimit}
                      onChange={(e) => setCreditLimit(parseFloat(e.target.value) || 0)}
                      onBlur={async () => {
                        try {
                          await api.put(`/v2/wallets/${id}/credit-toggle`, {
                            enabled: true, credit_limit: creditLimit,
                          });
                          toast.success("Credit limit updated");
                        } catch { toast.error("Update failed"); }
                      }}
                      data-testid="customer-credit-limit"
                      className="text-sm border border-slate-200 rounded px-2 py-1 focus:outline-none focus:border-brand-500 w-32" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 py-2 border-b border-slate-50 items-center">
                  <div className="text-xs text-slate-500 uppercase font-semibold" title="Also sets the monthly combined invoice billing cycle for this customer">Credit Terms</div>
                  <div className="col-span-2">
                    <select value={creditDays}
                      onChange={async (e) => {
                        const days = parseInt(e.target.value, 10);
                        setCreditDays(days);
                        try {
                          await api.put(`/v2/wallets/${id}/credit-toggle`, {
                            enabled: true, credit_days: days,
                          });
                          toast.success("Credit terms updated");
                        } catch { toast.error("Update failed"); }
                      }}
                      data-testid="customer-credit-days"
                      className="text-sm border border-slate-200 rounded px-2 py-1 focus:outline-none focus:border-brand-500">
                      <option value={7}>Net-7 (7 days)</option>
                      <option value={15}>Net-15 (15 days)</option>
                      <option value={30}>Net-30 (30 days)</option>
                    </select>
                  </div>
                </div>
              </>
            )}
            <div className="grid grid-cols-3 gap-2 py-2 items-center">
              <div className="text-xs text-slate-500 uppercase font-semibold">Platform Fee</div>
              <div className="col-span-2">
                {editing ? (
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!form.platform_fee_enabled}
                      onChange={(e) => onFieldChange("platform_fee_enabled", e.target.checked)}
                      data-testid="customer-platform-fee-toggle" />
                    Enabled
                  </label>
                ) : (
                  <span className="text-sm font-semibold text-slate-700">{form.platform_fee_enabled ? "Enabled" : "Disabled"}</span>
                )}
              </div>
            </div>
          </Card>

          <Card title="Bank & Rate Terms" icon={CreditCard} testid="customer-card-bank">
            <Field label="Account Holder" name="bank.account_holder_name" value={form["bank.account_holder_name"]} editing={editing} onChange={onFieldChange} />
            <Field label="Account Number" name="bank.account_number" value={form["bank.account_number"]} editing={editing} onChange={onFieldChange} />
            <Field label="IFSC" name="bank.ifsc_code" value={form["bank.ifsc_code"]} editing={editing} onChange={onFieldChange} />
            <Field label="Bank" name="bank.bank_name" value={form["bank.bank_name"]} editing={editing} onChange={onFieldChange} />
            <Field label="Markup %" name="rate_agreement.markup_percent" value={form["rate_agreement.markup_percent"]} editing={editing} onChange={onFieldChange} type="number" />
            <Field label="Rate Card File" value={profile.rate_agreement?.rate_card_filename || "—"} />
            <div className="grid grid-cols-3 gap-2 py-2">
              <div className="text-xs text-slate-500 uppercase font-semibold">Manual Rate Card</div>
              <div className="col-span-2 text-sm">
                {editing ? (
                  <label className="inline-flex items-center gap-2"><input type="checkbox" checked={!!form.manual_rate_card} onChange={(e) => onFieldChange("manual_rate_card", e.target.checked)} />
                    No agreement yet - price entered per booking</label>
                ) : (form.manual_rate_card ? <span className="text-amber-700 font-semibold">Yes - enter price per booking</span> : "No")}
              </div>
            </div>
          </Card>

          <Card title="Rate Agreement Summary" icon={FileText} testid="customer-card-agreement">
            {agreement ? (
              <>
                <Field label="FM Rate (₹/kg)" value={agreement.fm_lm_per_kg || "—"} />
                <Field label="LM Rate (₹/kg)" value={agreement.fm_lm_per_kg || "—"} />
                <Field label="MM Override" value={agreement.mm_override || "Use default"} />
                <Field label="LR Charge (₹)" value={agreement.lr_charge ?? 250} />
                <Field label="Counter-Drop FM Waiver" value={agreement.counter_drop_fm_waiver ? "Yes" : "No"} />
                <Field label="Counter-Collect LM Waiver" value={agreement.counter_collect_lm_waiver ? "Yes" : "No"} />
                <Field label="3PL Markup %" value={agreement.tpl_markup_pct ?? "Default 20%"} />
                <Link to={`/admin/rate-agreements?customer_id=${id}`}
                  className="text-brand-500 text-xs font-semibold mt-2 inline-block">View Full Agreement →</Link>
              </>
            ) : (
              <p className="text-sm text-slate-400 italic">No active rate agreement.
                <Link to={`/admin/rate-agreements?customer_id=${id}`} className="text-brand-500 ml-1">Create →</Link>
              </p>
            )}
          </Card>

          <Card title="Activity" icon={Activity} testid="customer-card-activity">
            <Field label="Total Shipments" value={profile.total_shipments ?? "—"} />
            <Field label="Total Spend (₹)" value={profile.total_spend?.toFixed?.(2) ?? "—"} />
            <Field label="Last Booking" value={profile.last_booking_date ? new Date(profile.last_booking_date).toLocaleDateString("en-IN") : "—"} />
            <div className="grid grid-cols-3 gap-2 py-2 border-b border-slate-50">
              <div className="text-xs text-slate-500 uppercase font-semibold">KYC Status</div>
              <div className="col-span-2">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                  profile.kyc_status === "VERIFIED" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                  profile.kyc_status === "REJECTED" ? "bg-red-50 text-red-700 border-red-200" :
                  "bg-amber-50 text-amber-700 border-amber-200"
                }`}>
                  {profile.kyc_status || "PENDING"}
                </span>
              </div>
            </div>
          </Card>
        </div>

        {history.length > 0 && (
          <Card title="Change History" icon={Activity} testid="customer-history">
            <div className="space-y-2 max-h-72 overflow-auto">
              {history.map((h) => (
                <div key={h.id} className="text-xs border-b border-slate-100 pb-2">
                  <b>{h.kind.replace("_", " ")}</b> by {h.by} ({h.by_role}) · {new Date(h.at).toLocaleString("en-IN")}
                  {h.reason && <div className="text-slate-500">Reason: {h.reason}</div>}
                  {h.comment && <div className="text-slate-500">Admin note: {h.comment}</div>}
                  {(h.changes || []).map((c, i) => <div key={i} className="text-slate-700">{c.label}: {String(c.old ?? "—")} → <b>{String(c.new)}</b></div>)}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Action buttons row */}
        {isAdmin && <div className="flex gap-3 flex-wrap">
          <Link to={`/admin/shipments?customer_id=${id}`} data-testid="customer-view-shipments"
            className="px-4 py-2 border border-slate-200 text-sm font-semibold rounded inline-flex items-center gap-2 hover:bg-slate-50">
            <Package className="w-4 h-4" /> View Shipments
          </Link>
          <Link to={`/admin/wallet-ledger-v2?customer_id=${id}`} data-testid="customer-view-wallet"
            className="px-4 py-2 border border-slate-200 text-sm font-semibold rounded inline-flex items-center gap-2 hover:bg-slate-50">
            <WalletIcon className="w-4 h-4" /> View Wallet
          </Link>
          <Link to={`/admin/invoices?customer_id=${id}`} data-testid="customer-view-invoices"
            className="px-4 py-2 border border-slate-200 text-sm font-semibold rounded inline-flex items-center gap-2 hover:bg-slate-50">
            <FileSignature className="w-4 h-4" /> View Invoices
          </Link>
          {/* Combine Invoices button removed from the customer card: invoice combining is automated (daily Zoho sync / monthly drafts). The /admin/combine-invoices page itself is untouched. */}
          <Link to={`/admin/rate-agreements?customer_id=${id}`} data-testid="customer-view-agreement"
            className="px-4 py-2 border border-slate-200 text-sm font-semibold rounded inline-flex items-center gap-2 hover:bg-slate-50">
            <FileSignature className="w-4 h-4" /> View Agreement
          </Link>
        </div>}
      </div>
    </DashboardLayout>
  );
}
